import { z } from 'zod';

import type { PropPickProps } from '~/domains/contest/domains/common/types/propPickProps.type';

const UserDirectionSchema = z.enum(['over', 'under']);
const WeatherIconSchema = z.enum([
  'rain',
  'snow',
  'wind',
  'cloud',
  'night',
  'indoor',
  'clear',
  'unknown',
]);

const PropPickRecordSchema = z.object({
  wins: z.number(),
  losses: z.number(),
  ties: z.number().optional(),
});

const PropPickLineupSchema = z.object({
  id: z.string(),
  inning: z.number(),
  order: z.number().optional(),
  unChecked: z.boolean().optional(),
});

const PropPickTeamSchema = z.object({
  id: z.string(),
  alias: z.string(),
  iconUrl: z.string().optional(),
  points: z.number().optional(),
  runs: z.number().optional(),
  record: PropPickRecordSchema.optional(),
  lineup: z.array(PropPickLineupSchema).optional(),
});

const PropPickChartDatumSchema = z.object({
  label: z.string(),
  value: z.number(),
  secondaryLabel: z.string().optional(),
});

const PropPickChartPropsSchema = z.object({
  chartData: z.array(PropPickChartDatumSchema),
  lastXAvg: z.string(),
  ticks: z.array(z.number()),
  tickCount: z.number(),
  referenceLineValue: z.number().optional(),
});

const PropPickStartDateAsDateSchema = z.preprocess((value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return value;
}, z.date());

export const PropPickSchema = z.object({
  id: z.string(),
  playerId: z.string(),
  league: z.string(),
  leagueImage: z.string(),
  playerImage: z.string(),
  playerName: z.string(),
  position: z.string().optional(),
  awayTeam: PropPickTeamSchema.optional(),
  homeTeam: PropPickTeamSchema.optional(),
  playerTeamId: z.string(),
  startDate: z.string(),
  startDateAsDate: PropPickStartDateAsDateSchema.optional(),
  gameDescription: z.string(),
  gameName: z.string().optional(),
  round: z.number().optional(),
  line: z.object({
    label: z.string(),
    value: z.number(),
    type: z.string().optional(),
    display: z.string().optional(),
  }),
  direction: UserDirectionSchema.optional(),
  chartProps: PropPickChartPropsSchema.optional(),
  useTeamAlias: z.boolean().optional(),
  teamAlias: z.string().optional(),
  teamColor: z.string().optional(),
  playerLineup: PropPickLineupSchema.optional(),
  injury: z.string().optional(),
  weatherIcon: WeatherIconSchema.optional(),
  isAlternate: z.boolean().optional(),
  overProbability: z.number().optional(),
  underProbability: z.number().optional(),
});

export const QuickPicksApiPickSchema = PropPickSchema;

export const normalizeMainPick = (pick: PropPickProps): PropPickProps & { isAlternate: false } => ({
  ...pick,
  isAlternate: false,
});

export const normalizeMainPicks = (
  picks: PropPickProps[]
): Array<PropPickProps & { isAlternate: false }> => picks.map((pick) => normalizeMainPick(pick));

export const normalizeAlternatePick = (
  pick: PropPickProps
): PropPickProps & { isAlternate: true } => ({
  ...pick,
  isAlternate: true,
});

export const QuickPicksStoredPicksSchema = z
  .array(PropPickSchema)
  .transform((picks) => normalizeMainPicks(picks as PropPickProps[]));

export type QuickPicksApiPick = z.infer<typeof QuickPicksApiPickSchema>;
export type QuickPicksStoredPick = PropPickProps;
